import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Activity, TrendingUp, TrendingDown, AlertCircle, MapPin, Trophy } from "lucide-react";

export default function BI() {
  const [bi, setBI] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/distributor/bi")
      .then(({ data }) => setBI(data))
      .catch((e) => setErr(e.response?.data?.detail || "Unable to load BI data"));
  }, []);
  if (err) return <div className="p-8 text-red-600" data-testid="bi-error">{err}</div>;
  if (!bi) return <div className="p-8 text-muted-foreground">Loading regional intelligence…</div>;

  const healthTone = bi.inventory_health_score >= 60 ? "text-emerald-700" : bi.inventory_health_score >= 30 ? "text-amber-700" : "text-red-700";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="distributor-bi">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Activity className="w-7 h-7 text-violet-600" /> Regional business intelligence</h1>
        <p className="text-muted-foreground">Demand, dealer performance and inventory health — refreshed every page load.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-5 text-center"><div className="label-overline">Inventory health</div><div className={`font-display text-5xl font-black mt-2 ${healthTone}`}>{bi.inventory_health_score}</div><div className="text-xs text-muted-foreground mt-1">+ for items &gt; 50 units, − for items &lt; 10</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="label-overline">Top region</div><div className="font-display text-2xl font-black mt-2">{Object.entries(bi.demand_by_region).sort((a,b)=>b[1]-a[1])[0]?.[0] || "—"}</div><div className="text-xs text-muted-foreground">₹{(Object.entries(bi.demand_by_region).sort((a,b)=>b[1]-a[1])[0]?.[1] || 0).toLocaleString()} last 30d</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="label-overline">Stock shortages predicted</div><div className="font-display text-3xl sm:text-4xl font-black mt-2 text-red-600">{bi.predicted_shortages.length}</div><div className="text-xs text-muted-foreground">items below 30-day velocity</div></CardContent></Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardContent className="p-5">
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-600" /> Dealer ranking · last 30 days</h2>
          <Table>
            <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Dealer</TableHead><TableHead>Tier</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
            <TableBody>
              {bi.dealer_ranking.slice(0, 8).map((d, i) => (
                <TableRow key={d.dealer_id} data-testid={`bi-dealer-${d.dealer_id}`}>
                  <TableCell className="font-mono">{i + 1}</TableCell>
                  <TableCell><div className="font-semibold">{d.name}</div><div className="text-[10px] text-muted-foreground">{d.region}</div></TableCell>
                  <TableCell><Badge variant="outline">{d.tier}</Badge></TableCell>
                  <TableCell className="text-right font-semibold">₹{d.revenue.toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {bi.dealer_ranking.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No paid orders yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>

        <Card><CardContent className="p-5">
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" /> Fast movers</h2>
          {bi.fast_movers.length === 0 ? <div className="text-muted-foreground text-sm">No movement yet.</div> : (
            <ul className="space-y-1 text-sm">
              {bi.fast_movers.slice(0,8).map((p) => (
                <li key={p.product_id} className="flex justify-between"><span className="truncate">{p.title}</span><span className="font-mono font-semibold">{p.qty_moved}</span></li>
              ))}
            </ul>
          )}
          <h2 className="font-display text-lg font-bold mb-3 mt-5 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-amber-600" /> Slow movers</h2>
          {bi.slow_movers.length === 0 ? <div className="text-muted-foreground text-sm">—</div> : (
            <ul className="space-y-1 text-sm">
              {bi.slow_movers.slice(0,5).map((p) => (
                <li key={p.product_id} className="flex justify-between"><span className="truncate">{p.title}</span><span className="font-mono">{p.qty_moved}</span></li>
              ))}
            </ul>
          )}
        </CardContent></Card>
      </div>

      <Card><CardContent className="p-5">
        <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-600" /> Predicted stock shortages (next 30 days)</h2>
        <Table>
          <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Current</TableHead><TableHead>Monthly velocity</TableHead><TableHead>Days remaining</TableHead></TableRow></TableHeader>
          <TableBody>
            {bi.predicted_shortages.map((s) => (
              <TableRow key={s.item_id} data-testid={`bi-short-${s.item_id}`}>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.current}</TableCell>
                <TableCell>{s.monthly_velocity}</TableCell>
                <TableCell><Badge variant="outline" className={s.days_remaining < 7 ? "bg-red-100 text-red-900" : ""}>{s.days_remaining} days</Badge></TableCell>
              </TableRow>
            ))}
            {bi.predicted_shortages.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No imminent shortages — stock looks healthy.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Card><CardContent className="p-5">
        <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Demand by region</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {Object.entries(bi.demand_by_region).map(([region, rev]) => (
            <div key={region} className="surface-card p-3" data-testid={`bi-region-${region}`}>
              <div className="text-sm text-muted-foreground">{region}</div>
              <div className="font-display text-2xl font-black mt-1">₹{rev.toLocaleString()}</div>
            </div>
          ))}
          {Object.keys(bi.demand_by_region).length === 0 && <div className="text-muted-foreground text-sm col-span-3 text-center py-4">No regional revenue data yet.</div>}
        </div>
      </CardContent></Card>
    </div>
  );
}
